import { createReducer, on } from '@ngrx/store';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { WithdrawalRequestStatusType } from '../../models/enums/wallet.enum';
import {
    WalletPaymentAccount,
    WalletPaymentTransaction,
    WithdrawalRequestsWithPagination,
} from '../../models/wallet.interface';
import { WalletActions } from '../actions/actions-type.action';

export interface WalletState {
    paymentAccounts: WalletPaymentAccount[];
    transactions: WalletPaymentTransaction[];
    currentTeamTransactions: WalletPaymentTransaction[];
    LPTransactions: WalletPaymentTransaction[];
    currentTeamWallet: WalletPaymentAccount | null;
    withdrawalRequests: WithdrawalRequestsWithPagination | null;
    pendingWithdrawalRequestsTotal: number | null;
    requestedStatus: WithdrawalRequestStatusType | null | undefined;
    error: BadResponseWithError | null;
    isLoading: boolean;
    cryptoWalletNetwork: string | null;
    cryptoWalletChainId: string | null;
    cryptoWalletAddress: string | null;
    cryptoWalletName: string | null;
    cryptoWalletId: string | null;
}

const initialState: WalletState = {
    paymentAccounts: [],
    transactions: [],
    currentTeamTransactions: [],
    LPTransactions: [],
    currentTeamWallet: null,
    withdrawalRequests: null,
    pendingWithdrawalRequestsTotal: null,
    requestedStatus: null,
    error: null,
    isLoading: false,
    cryptoWalletNetwork: null,
    cryptoWalletChainId: null,
    cryptoWalletAddress: null,
    cryptoWalletName: null,
    cryptoWalletId: null,
};

const sortByTransactionAtDesc = (items: WalletPaymentTransaction[] = []) => {
    try {
        return [...items].sort((a, b) => {
            const ta = a && (a as any).transactionAt;
            const tb = b && (b as any).transactionAt;
            const da = ta ? new Date(ta).getTime() : 0;
            const db = tb ? new Date(tb).getTime() : 0;
            return db - da;
        });
    } catch {
        return [...(items || [])];
    }
};

export const walletReducer = createReducer(
    initialState,
    on(WalletActions.fetchCryptoWalletSuccess, (state,action) => {
        return {
            ...state,
            cryptoWalletNetwork: action.payload.cryptoWalletNetwork,
            cryptoWalletChainId: action.payload.cryptoWalletChainId,
            cryptoWalletAddress: action.payload.cryptoWalletAddress,
            cryptoWalletName: action.payload.cryptoWalletName,
            cryptoWalletId: action.payload.cryptoWalletId,
        }

    }),
    on(WalletActions.connectCryptoWalletSuccess, (state,action) => {
        return {
            ...state,
            cryptoWalletNetwork: action.payload.cryptoWalletNetwork,
            cryptoWalletChainId: action.payload.cryptoWalletChainId,
            cryptoWalletAddress: action.payload.cryptoWalletAddress,
            cryptoWalletName: action.payload.cryptoWalletName,
            cryptoWalletId: action.payload.cryptoWalletId,

        }
    }),
    on(WalletActions.fetchCryptoWalletFailure, (state,action) => ({
        ...state,
        cryptoWalletNetwork: null,
        cryptoWalletChainId: null,
        cryptoWalletAddress: null,
        cryptoWalletName: null,
        cryptoWalletId: null,
    })),
    on(WalletActions.disconnectCryptoWalletSuccess, (state,action) => ({
        ...state,
        cryptoWalletNetwork: null,
        cryptoWalletChainId: null,
        cryptoWalletAddress: null,
        cryptoWalletName: null,
        cryptoWalletId: null,
    })),

    on(WalletActions.fetchProfilePaymentAccounts, (state) => ({
        ...state,
        paymentAccounts: [],
        isLoading: true,
    })),


    on(WalletActions.fetchProfilePaymentAccountsSuccess, (state, action) => ({
        ...state,
        paymentAccounts: action.payload,
        isLoading: false,
    })),
    on(WalletActions.fetchProfilePaymentAccountsFailure, (state, action) => ({
        ...state,
        error: action.payload,
        isLoading: false,
    })),
    on(WalletActions.fetchWalletTransactions, (state) => ({
        ...state,
        isLoading: true,
    })),

    on(WalletActions.fetchWalletTransactionsSuccess, (state, action) => ({
        ...state,
        transactions: sortByTransactionAtDesc(action.payload),
        isLoading: false,
    })),
    on(WalletActions.fetchWalletTransactionsFailure, (state, action) => ({
        ...state,
        error: action.payload,
        isLoading: false,
    })),
    on(WalletActions.fetchLPWalletTransactions, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(WalletActions.fetchLPWalletTransactionsSuccess, (state, action) => ({
        ...state,
        LPTransactions: sortByTransactionAtDesc(action.payload),
        isLoading: false,
    })),
    on(WalletActions.fetchLPWalletTransactionsFailure, (state, action) => ({
        ...state,
        error: action.payload,
        isLoading: false,
    })),
    on(WalletActions.fetchWithdrawalRequests, (state, action) => ({
        ...state,
        requestedStatus: action.payload?.status,
        isLoading: true,
    })),
    on(WalletActions.fetchWithdrawalRequestsSuccess, (state, action) => {
        let pendingWithdrawalRequestsTotal =
            state.pendingWithdrawalRequestsTotal;

        if (state.requestedStatus === WithdrawalRequestStatusType.Pending) {
            pendingWithdrawalRequestsTotal = action.payload.items.total;
        }

        return {
            ...state,
            withdrawalRequests: action.payload.items,
            pendingWithdrawalRequestsTotal,
            isLoading: false,
        };
    }),
    on(WalletActions.fetchWithdrawalRequestsFailure, (state, action) => ({
        ...state,
        error: action.payload,
        isLoading: false,
    })),
    on(WalletActions.sendWithdrawalRequestFailure, (state, action) => ({
        ...state,
        error: action.payload,
        isLoading: false,
    })),
    on(WalletActions.sendWithdrawalRequestSuccess, (state, action) => ({
        ...state,
        isLoading: false,
    })),
    on(WalletActions.sendWithdrawalRequest, (state, action) => {
        try {
            // console.log('[WithdrawReducer]', 'sendWithdrawalRequest observed', {
            //     setIsLoading: true,
            //     prevLoading: state.isLoading,
            // });
        } catch {}
        return {
            ...state,
            isLoading: true,
        };
    }),

    on(WalletActions.signalRWithdrawalRequestAdded, (state, action) => {
        let withdrawalRequests = state.withdrawalRequests;

        if (
            action.payload.request.status ===
                WithdrawalRequestStatusType.Pending &&
            state.requestedStatus === WithdrawalRequestStatusType.Pending
        ) {
            withdrawalRequests = {
                ...state.withdrawalRequests,
                items: [
                    action.payload.request,
                    ...(
                        state.withdrawalRequests as WithdrawalRequestsWithPagination
                    ).items,
                ],
            } as WithdrawalRequestsWithPagination;
        }

        return {
            ...state,
            withdrawalRequests,
            pendingWithdrawalRequestsTotal:
                ((state.pendingWithdrawalRequestsTotal ?? 0) as number) + 1,
        };
    }),
    on(WalletActions.signalRWithdrawalRequestApproved, (state, action) => ({
        ...state,
    })),

    //TEAM WALLET
    on(WalletActions.fetchTeamLPWallet, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(WalletActions.fetchTeamLPWalletSuccess, (state, action) => ({
        ...state,
        isLoading: false,
        currentTeamWallet: action.payload,
    })),
    on(WalletActions.fetchTeamLPWalletFailure, (state, action) => ({
        ...state,
        isLoading: false,
        currentTeamWallet: null,
    })),
    on(WalletActions.fetchTeamLPWalletTransactions, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(WalletActions.fetchTeamLPWalletTransactionsSuccess, (state, action) => ({
        ...state,
        isLoading: false,
        currentTeamTransactions: sortByTransactionAtDesc(action.payload),
    })),

    on(WalletActions.fetchTeamLPWalletTransactionsFailure, (state, action) => ({
        ...state,
        isLoading: false,
        currentTeamTransactions: [],
    })),
    on(WalletActions.depositLPRequest, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(WalletActions.depositLPSuccess, (state) => ({
        ...state,
        isLoading: false,
    })),
    on(WalletActions.depositLPFailure, (state, action) => ({
        ...state,
        error: action.payload,
        isLoading: false,
    })),

);
